import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';
import { useSettingsStore } from './settingsStore';
import { addAlert } from './alertsStore';

export type RoundStatus = 'draft' | 'programmed' | 'open' | 'closed';
export type PartyRingState = 'active' | 'warned' | 'blocked';
export type FactCheckStatus = 'pending' | 'approved' | 'rejected';

export interface FactCheckRequest {
  id: string;
  argumentId: string;
  partyAcronym: string;
  sourceUrl: string;
  explanation: string;
  status: FactCheckStatus;
  submittedAt: string;
  submittedByUserId?: string;
}

export interface RingArgument {
  id: string;
  roundId: string;
  partyAcronym: string;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  reactions?: {
    convincing: number;
    sourced: number;
    demagogic: number;
    offtopic: number;
  };
}

export interface RingRound {
  id: string;
  roundNumber: number;
  category: string;
  questionFr: string;
  questionAr: string;
  questionAmz: string;
  status: RoundStatus;
  startDate?: string;
  endDate?: string;
  daysLeft?: number;
}

export interface PartyRingStatus {
  partyAcronym: string;
  hasExportAccess: boolean;
  status: PartyRingState;
  warningMessage?: string;
  charterSignedAt?: string;
}

interface RingStore {
  rounds: RingRound[];
  factChecks: FactCheckRequest[];
  partyStatuses: PartyRingStatus[];
  arguments: RingArgument[];
  userReactions: Record<string, string>;
  isRingEnabled: boolean;
  isFactCheckEnabled: boolean;
  isRingResultsEnabled: boolean;
  
  setRounds: (rounds: RingRound[]) => void;
  setFactChecks: (factChecks: FactCheckRequest[]) => void;
  setPartyStatuses: (partyStatuses: PartyRingStatus[]) => void;
  setArguments: (args: RingArgument[]) => void;
  setUserReactions: (reactions: Record<string, string>) => void;
  toggleRingEnabled: (enabled: boolean) => void;
  toggleFactCheckEnabled: (enabled: boolean) => void;
  toggleRingResultsEnabled: (enabled: boolean) => void;
}

export const useRingStore = create<RingStore>((set) => ({
  rounds: [],
  factChecks: [],
  partyStatuses: [],
  arguments: [],
  userReactions: {},
  isRingEnabled: true,
  isFactCheckEnabled: true,
  isRingResultsEnabled: true,
  
  setRounds: (rounds) => set({ rounds }),
  setFactChecks: (factChecks) => set({ factChecks }),
  setPartyStatuses: (partyStatuses) => set({ partyStatuses }),
  setArguments: (args) => set({ arguments: args }),
  setUserReactions: (userReactions) => set({ userReactions }),
  toggleRingEnabled: (enabled) => {
    set({ isRingEnabled: enabled });
    useSettingsStore.getState().updateSetting('ring_enabled', enabled);
  },
  toggleFactCheckEnabled: (enabled) => {
    set({ isFactCheckEnabled: enabled });
    useSettingsStore.getState().updateSetting('ring_fact_check_enabled', enabled);
  },
  toggleRingResultsEnabled: (enabled) => {
    set({ isRingResultsEnabled: enabled });
    useSettingsStore.getState().updateSetting('ring_results_enabled', enabled);
  }
}));

export const fetchRingDataFromServer = async () => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php?action=all&t=${Date.now()}`);
    if (!res.ok) throw new Error("API Error");
    const data = await res.json();
    
    if (data.rounds) {
      useRingStore.getState().setRounds(data.rounds.map((r: any) => ({
        id: r.id,
        roundNumber: Number(r.round_number),
        category: r.category,
        questionFr: r.question_fr,
        questionAr: r.question_ar,
        questionAmz: r.question_amz,
        status: r.status as RoundStatus,
        startDate: r.start_date,
        endDate: r.end_date
      })));
    }
    
    if (data.factChecks) {
      useRingStore.getState().setFactChecks(data.factChecks.map((fc: any) => ({
        id: fc.id,
        argumentId: fc.argument_id,
        partyAcronym: fc.party_acronym,
        sourceUrl: fc.source_url,
        explanation: fc.explanation,
        status: fc.status as FactCheckStatus,
        submittedAt: fc.submitted_at
      })));
    }

    if (data.partyStatuses) {
      useRingStore.getState().setPartyStatuses(data.partyStatuses.map((p: any) => ({
        partyAcronym: p.party_acronym,
        hasExportAccess: p.has_export_access === 1 || p.has_export_access === "1",
        status: p.status as PartyRingState,
        warningMessage: p.warning_message,
        charterSignedAt: p.charter_signed_at
      })));
    }
    
    if (data.arguments) {
      useRingStore.getState().setArguments(data.arguments.map((a: any) => ({
        id: a.id,
        roundId: a.round_id,
        partyAcronym: a.party_acronym,
        content: a.content,
        status: a.status,
        createdAt: a.created_at,
        reactions: a.reactions || { convincing: 0, sourced: 0, demagogic: 0, offtopic: 0 }
      })));
    }

    if (data.userReactions) {
      useRingStore.getState().setUserReactions(data.userReactions);
    }
    
    if (data.settings) {
      if (data.settings.ring_enabled !== undefined) {
        useRingStore.getState().toggleRingEnabled(data.settings.ring_enabled === "true");
      }
      if (data.settings.ring_fact_check_enabled !== undefined) {
        useRingStore.getState().toggleFactCheckEnabled(data.settings.ring_fact_check_enabled === "true");
      }
      if (data.settings.ring_results_enabled !== undefined) {
        useRingStore.getState().toggleRingResultsEnabled(data.settings.ring_results_enabled === "true");
      }
    }
  } catch (e) {
    console.error("Failed to fetch ring data from server", e);
  }
};

if (typeof window !== "undefined") {
  fetchRingDataFromServer();
}

// Retro-compatibility functions for UI components
export const getRingRounds = () => useRingStore.getState().rounds;
export const getFactChecks = () => useRingStore.getState().factChecks;
export const getPartyRingStatuses = () => useRingStore.getState().partyStatuses;
export const getRingArguments = () => useRingStore.getState().arguments;

export const getPartyRingStatus = (acronym: string): PartyRingStatus => {
  const p = useRingStore.getState().partyStatuses.find(x => x.partyAcronym === acronym);
  return p || { partyAcronym: acronym, hasExportAccess: false, status: 'active' };
};

export const saveRingRound = async (r: RingRound) => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save_round', round: r })
    });
    if (!res.ok) throw new Error("Failed to save round");
    
    const rounds = useRingStore.getState().rounds;
    const existingIndex = rounds.findIndex(x => x.id === r.id);
    const newRounds = [...rounds];
    if (existingIndex >= 0) newRounds[existingIndex] = r;
    else newRounds.push(r);
    useRingStore.getState().setRounds(newRounds);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const updateRingRoundStatus = async (id: string, status: RoundStatus) => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update_round_status', id, status })
    });
    if (!res.ok) throw new Error("Failed to update round status");

    const rounds = useRingStore.getState().rounds;
    const newRounds = rounds.map(r => r.id === id ? { ...r, status } : r);
    useRingStore.getState().setRounds(newRounds);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const submitFactCheck = async (request: Omit<FactCheckRequest, 'id' | 'status' | 'submittedAt'>) => {
  const newFC: FactCheckRequest = {
    ...request,
    id: `fc${Date.now()}`,
    status: 'pending',
    submittedAt: new Date().toISOString()
  };
  
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'submit_fact_check', factCheck: newFC })
    });
    if (!res.ok) throw new Error("Failed to submit fact check");

    useRingStore.getState().setFactChecks([newFC, ...useRingStore.getState().factChecks]);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const submitRingArgument = async (roundId: string, partyAcronym: string, content: string) => {
  const newArg: RingArgument = {
    id: `arg_${Date.now()}`,
    roundId,
    partyAcronym,
    content,
    status: 'approved',
    createdAt: new Date().toISOString()
  };
  
  try {
    const token = localStorage.getItem('kaynin_auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'submit_argument', argument: newArg })
    });
    if (!res.ok) throw new Error("Failed to submit argument");

    const currentArgs = useRingStore.getState().arguments;
    const filtered = currentArgs.filter(a => !(a.roundId === roundId && a.partyAcronym === partyAcronym));
    useRingStore.getState().setArguments([newArg, ...filtered]);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const submitReaction = async (argumentId: string, reactionType: string) => {
  const token = localStorage.getItem('kaynin_auth_token');
  if (!token) return;

  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ action: 'submit_reaction', argumentId, reactionType })
    });
    if (!res.ok) throw new Error("Failed to submit reaction");

    const currentReactions = { ...useRingStore.getState().userReactions };
    const currentArgs = [...useRingStore.getState().arguments];
    const argIndex = currentArgs.findIndex(a => a.id === argumentId);

    if (argIndex >= 0) {
      const oldReaction = currentReactions[argumentId];
      const newArgs = [...currentArgs];
      const reactions = { ...newArgs[argIndex].reactions! };
      
      if (oldReaction) {
        reactions[oldReaction as keyof typeof reactions]--;
      }
      
      if (reactionType === 'remove') {
        delete currentReactions[argumentId];
      } else {
        reactions[reactionType as keyof typeof reactions]++;
        currentReactions[argumentId] = reactionType;
      }
      
      newArgs[argIndex] = { ...newArgs[argIndex], reactions };
      useRingStore.getState().setArguments(newArgs);
      useRingStore.getState().setUserReactions(currentReactions);
    }
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const updateFactCheckStatus = async (id: string, status: FactCheckStatus) => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update_fact_check_status', id, status })
    });
    if (!res.ok) throw new Error("Failed to update fact check status");

    const factChecks = useRingStore.getState().factChecks;
    const newFC = factChecks.map(fc => fc.id === id ? { ...fc, status } : fc);
    useRingStore.getState().setFactChecks(newFC);
    
    const updatedFC = newFC.find(fc => fc.id === id);
    if (status === 'approved' && updatedFC?.submittedByUserId) {
      addAlert({
          title: { fr: "Fact-Check Publié", ar: "تم نشر التحقق", zgh: "ⵉⵜⵜⵓⴼⵙⴰⵔ ⵓⵙⵉⴷⴷ" },
          description: { 
            fr: "Votre publication sur Le Ring a été publiée.", 
            ar: "تم نشر مساهمتك في حلبة النقاش.", 
            zgh: "ⵜⴻⵜⵜⵓⴼⵙⴰⵔ ⵜⵎⵔⵔⵓⵜ ⵏⵏⴽ ⴳ ⵓⴱⴰⵔⴰⵣ." 
          },
          sourceName: "Modération Le Ring",
          sourceType: "Institution",
          region: "Toutes",
          theme: "Politique",
          isImportant: false,
          targetUserId: updatedFC.submittedByUserId,
          notificationType: "ring_approved"
        });
    }
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const togglePremiumAccess = async (acronym: string) => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'toggle_premium_access', acronym })
    });
    if (!res.ok) throw new Error("Failed to toggle premium access");

    const statuses = useRingStore.getState().partyStatuses;
    const existing = statuses.find(x => x.partyAcronym === acronym);
    let newStatuses;
    if (existing) {
      newStatuses = statuses.map(s => s.partyAcronym === acronym ? { ...s, hasExportAccess: !s.hasExportAccess } : s);
    } else {
      newStatuses = [...statuses, { partyAcronym: acronym, hasExportAccess: true, status: 'active' as PartyRingState }];
    }
    useRingStore.getState().setPartyStatuses(newStatuses);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const updatePartyRingState = async (acronym: string, status: PartyRingState, warningMessage?: string) => {
  try {
    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update_party_state', acronym, status, warningMessage })
    });
    if (!res.ok) throw new Error("Failed to update party ring state");

    const statuses = useRingStore.getState().partyStatuses;
    const existing = statuses.find(x => x.partyAcronym === acronym);
    let newStatuses;
    if (existing) {
      newStatuses = statuses.map(s => s.partyAcronym === acronym ? { ...s, status, warningMessage } : s);
    } else {
      newStatuses = [...statuses, { partyAcronym: acronym, hasExportAccess: false, status, warningMessage }];
    }
    useRingStore.getState().setPartyStatuses(newStatuses);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};

export const signRingCharter = async (acronym: string) => {
  try {
    const token = localStorage.getItem('kaynin_auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${getApiUrl()}/ring.php`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ action: 'sign_charter', acronym })
    });
    if (!res.ok) throw new Error("Failed to sign charter");

    const statuses = useRingStore.getState().partyStatuses;
    const existing = statuses.find(x => x.partyAcronym === acronym);
    const now = new Date().toISOString();
    let newStatuses;
    if (existing) {
      newStatuses = statuses.map(s => s.partyAcronym === acronym ? { ...s, charterSignedAt: now } : s);
    } else {
      newStatuses = [...statuses, { partyAcronym: acronym, hasExportAccess: false, status: 'active' as PartyRingState, charterSignedAt: now }];
    }
    useRingStore.getState().setPartyStatuses(newStatuses);
  } catch (e) { 
    console.error(e); 
    throw e;
  }
};
